import { useRef } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { v4 as uuidV4 } from "uuid";

import {
  informPreparingDownload,
  informPreparingLink,
  pollAndNotifyDownloadSuccess,
  pollAndNotifyLinkSuccess,
} from "src/api/downloadUtils";
import { ExporterDatasetJobsEndpoint, simulationsApi } from "src/api/endpoints";
import { RunSimulationRequest } from "src/clients/simulations/api";
import { insertNewJobInCache } from "src/datasets/api/queries";
import { toastActions } from "src/design-system/Toast/utils";
import { jobRunsKeys } from "src/jobs/api/queries";
import { toastError } from "src/utils/toastError";

export const markAllSimulationsAsDeleted = async (
  baseURL: string,
  simulationJobRunIds: string[],
) => {
  const api = simulationsApi(baseURL);
  // Need to be deleted sequentially for now, see OPTI-2635
  const results = [];
  for (const id of simulationJobRunIds) {
    const result =
      await api.markSimulationAsDeletedSimulationsApiV1SimulationsSimulationJobRunIdDelete(
        {
          simulationJobRunId: id,
        },
      );
    results.push(result);
  }
  return results;
};

export const useMarkSimulationAsDeleted = (
  baseURL: string,
  simulationJobId: string,
) => {
  const queryClient = useQueryClient();
  const api = simulationsApi(baseURL);

  return useMutation({
    mutationFn: (simulationJobRunId: string) =>
      api.markSimulationAsDeletedSimulationsApiV1SimulationsSimulationJobRunIdDelete(
        {
          simulationJobRunId,
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: jobRunsKeys.job(baseURL, simulationJobId),
      });
      toastActions.success({ title: "Simulation deleted successfully" });
    },
    onError: () => {
      toastActions.failure({ title: "Failed to delete simulation" });
    },
  });
};

export const useStartSimulationRun = (baseURL: string) => {
  const queryClient = useQueryClient();
  const api = simulationsApi(baseURL);

  return useMutation({
    mutationFn: (request: RunSimulationRequest) =>
      api.runSimulationSimulationsApiV1SimulationsPost({
        runSimulationRequest: request,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: jobRunsKeys.job(baseURL, response.data.job_id),
      });
      toastActions.loading({
        title: "Simulation is running",
        description: "This tab may be closed. Results will be available later.",
      });
    },
    onError: (error) => {
      toastError(error);
    },
  });
};

export const useMarkAllSimulationsAsDeleted = (
  baseURL: string,
  simulationJobId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (simulationJobRunIds: string[]) =>
      await markAllSimulationsAsDeleted(baseURL, simulationJobRunIds),
    onSuccess: () => {
      // Invalidate runs, using the exact same key as in useRuns
      queryClient.invalidateQueries({
        queryKey: jobRunsKeys.job(baseURL, simulationJobId),
      });
      toastActions.success({ title: "All simulations deleted successfully" });
    },
    onError: () => {
      toastActions.failure({ title: "Failed to delete all simulations" });
    },
  });
};

export const useCreateFailedSimulationDatasetJob = (
  baseURL: string,
  flow_id: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      simulationJobRunId,
      name,
    }: {
      simulationJobRunId: string;
      name: string;
    }) => {
      return ExporterDatasetJobsEndpoint.createDatasetJob(baseURL, {
        type: "simulation_failures",
        body: {
          flow_id,
          request: {
            simulation_job_run_id: simulationJobRunId,
            name: name,
          },
        },
      });
    },
    onSuccess: (newJob) => {
      insertNewJobInCache(baseURL, newJob, queryClient);
      toastActions.loading({
        title: "Creating failed decisions dataset",
        description:
          "This tab may be closed. The dataset will be available later.",
      });
    },
    onError: () => {
      toastActions.failure({
        title: "Failed to create failed decisions dataset",
      });
    },
  });
};

export const useCreateSimulationDownloadDatasetJob = (
  baseURL: string,
  flow_id: string,
) => {
  const queryClient = useQueryClient();
  const toastIdRef = useRef<string>();

  return useMutation({
    mutationFn: async ({
      simulationJobRunId,
      format,
    }: {
      simulationJobRunId: string;
      format: "csv" | "ndjson";
    }) => {
      const toastId = uuidV4();
      toastIdRef.current = toastId;
      informPreparingDownload(toastId);

      const downloadJob = await ExporterDatasetJobsEndpoint.createDatasetJob(
        baseURL,
        {
          type: "simulation_download",
          body: {
            flow_id,
            request: {
              simulation_job_run_id: simulationJobRunId,
              format,
            },
          },
        },
      );

      insertNewJobInCache(baseURL, downloadJob, queryClient);

      pollAndNotifyDownloadSuccess(
        downloadJob,
        baseURL,
        toastId,
        ExporterDatasetJobsEndpoint.getDatasetJob,
      ).catch(() => {
        toastActions.failure({
          id: toastId,
          title: "Failed to prepare simulation download",
        });
      });

      return downloadJob;
    },
    onError: () => {
      toastActions.failure({
        id: toastIdRef.current,
        title: "Failed to prepare simulation download",
      });
    },
  });
};

export const useCreateSimulationLinkDatasetJob = (
  baseURL: string,
  flow_id: string,
) => {
  const queryClient = useQueryClient();
  const toastIdRef = useRef<string>();

  return useMutation({
    mutationFn: async ({
      simulationJobRunId,
      format,
    }: {
      simulationJobRunId: string;
      format: "csv" | "ndjson";
    }) => {
      const toastId = uuidV4();
      toastIdRef.current = toastId;
      informPreparingLink(toastId);

      const downloadJob = await ExporterDatasetJobsEndpoint.createDatasetJob(
        baseURL,
        {
          type: "simulation_download",
          body: {
            flow_id,
            request: {
              simulation_job_run_id: simulationJobRunId,
              format,
            },
          },
        },
      );

      insertNewJobInCache(baseURL, downloadJob, queryClient);

      pollAndNotifyLinkSuccess(
        downloadJob,
        baseURL,
        toastId,
        ExporterDatasetJobsEndpoint.getDatasetJob,
      ).catch(() => {
        toastActions.failure({
          id: toastId,
          title: "Failed to prepare simulation link",
        });
      });

      return downloadJob;
    },
    onError: () => {
      toastActions.failure({
        id: toastIdRef.current,
        title: "Failed to prepare simulation link",
      });
    },
  });
};
